import { useQuery } from "@apollo/client";
import { CARAC_MAX, PLAYER_CARAC } from "enefel/player";
import React, { FC } from "react";
import styled from "styled-components";
import { GET_STATS_ADMIN } from "../../query/PlayerQueries";
import { GET_ME } from "../../query/UserQueries";
import { ADMIN_USERNAME } from "../Chart/Charts";
import QueryError from "../Error/QueryError";
import Loading from "../Loading";
import { H1, H2 } from "../Title";

const Container = styled.div`
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const Th = styled.th`
  border: 1px solid #ccc;
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  text-align: left;
`;

const Td = styled.td<{ outOfBounds?: boolean }>`
  border: 1px solid #ccc;
  padding: 0.4rem 0.75rem;
  background: ${(p) => (p.outOfBounds ? "#ffcccc" : "transparent")};
  font-weight: ${(p) => (p.outOfBounds ? "bold" : "normal")};
`;

const ImprovementList = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  font-size: 0.8rem;
`;

const Badge = styled.span`
  display: inline-block;
  background: #e53935;
  color: white;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
`;

type Improvement = {
  roll_number: number;
  d1: number;
  d2: number;
  skill_id: string | null;
};

type PlayerStat = {
  id: string;
  name: string;
  race_id: string;
  MA: number;
  ST: number;
  AG: number;
  PA: number | null;
  AV: number;
  improvements: Improvement[];
};

const StatCell: FC<{ value: number; carac: keyof typeof CARAC_MAX }> = ({ value, carac }) => {
  const isOut = value > CARAC_MAX[carac as keyof typeof CARAC_MAX];
  return <Td outOfBounds={isOut}>{value}{isOut && <Badge>!</Badge>}</Td>;
};

const StatsAdmin: FC = () => {
  const { loading: loadingMe, data: dataMe } = useQuery(GET_ME);
  const { loading, error, data } = useQuery(GET_STATS_ADMIN);

  if (loadingMe || loading) return <Loading />;
  if (error) return <QueryError error={error} />;

  if (dataMe?.me?.username !== ADMIN_USERNAME) {
    return (
      <Container>
        <H1>Accès non autorisé</H1>
      </Container>
    );
  }

  const players: PlayerStat[] = data?.statsAdmin ?? [];

  return (
    <Container>
      <H1>Stats hors limites <Badge>{players.length}</Badge></H1>
      {players.length === 0 ? (
        <p>Aucun joueur avec des stats hors limites.</p>
      ) : (
        <>
          <H2>Joueurs concernés</H2>
          <Table>
            <thead>
              <tr>
                <Th>Joueur</Th>
                <Th>Race</Th>
                <Th>MA (max 9)</Th>
                <Th>ST (max 8)</Th>
                <Th>AG (max 5)</Th>
                <Th>PA</Th>
                <Th>AV (max 10)</Th>
                <Th>Améliorations</Th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.id}>
                  <Td>{p.name}</Td>
                  <Td>{p.race_id}</Td>
                  <StatCell value={p.MA} carac={PLAYER_CARAC.MA} />
                  <StatCell value={p.ST} carac={PLAYER_CARAC.ST} />
                  <StatCell value={p.AG} carac={PLAYER_CARAC.AG} />
                  <Td>{p.PA ?? "-"}</Td>
                  <StatCell value={p.AV} carac={PLAYER_CARAC.AV} />
                  <Td>
                    <ImprovementList>
                      {p.improvements.map((i) => (
                        <li key={i.roll_number}>
                          #{i.roll_number} — {i.d1}+{i.d2}={i.d1 + i.d2}
                          {i.skill_id ? ` → ${i.skill_id}` : " (en attente)"}
                        </li>
                      ))}
                    </ImprovementList>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
};

export default StatsAdmin;
